import React from 'react';
import { Container, Section } from '../theme';
import { useSharedContent } from '../providers/ContentProvider';
import Navbar from '../components/layout/Navbar';
import ProgressiveImage from '../components/shared/ProgressiveImage';
import SEOHead from '../components/shared/SEOHead';
import { BreadcrumbStructuredData, ServicesStructuredData } from '../components/shared/StructuredData';
import { inferMediaType } from '../lib/mediaUtils';

const ProductsPage: React.FC = () => {
  const { content, loading } = useSharedContent();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-gray-700">Loading...</p>
      </div>
    );
  }

  const items = (content.gallery.items || []).map((item) => ({
    ...item,
    blurhash: item.blurhash ?? 'LEHV6nWB2yk8pyo0adR*.7kCMdnj',
  }));

  return (
    <div className="min-h-screen body-font w-full overflow-x-hidden bg-surface-bg text-ink">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:right-2 focus:z-[9999] focus:bg-brand focus:text-white focus:px-4 focus:py-2 focus:rounded-lg">
        דלג לתוכן הראשי
      </a>
      <SEOHead
        title="השירותים שלנו | Touch Event - עיסוי רפואי לאירועים"
        description="חבילות עיסוי מקצועי לאירועים ומשרדים: עיסוי שוודי, רפלקסולוגיה, כוסות רוח, עיסוי משולב ואלקטרותרפיה. התאמה אישית לכל ארגון."
        path="/products"
      />
      <BreadcrumbStructuredData items={[
        { name: 'דף הבית', url: 'https://touchevent.site/' },
        { name: 'השירותים שלנו', url: 'https://touchevent.site/products' },
      ]} />
      <ServicesStructuredData gallery={content.gallery} />
      <Navbar brand={content.brand} logo={content.logo} nav={content.nav} social={content.social} />
      <main id="main-content" role="main">
      <Section id="products" className="pt-20">
        <Container>
          <div className="mx-auto max-w-6xl">
            {/* Back Button */}
            <div className="mb-8 md:mb-10">
              <a
                href="/"
                className="inline-flex items-center gap-2 text-sm menu-font text-ink-muted hover:text-brand transition-colors duration-200"
                dir="rtl"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 rtl:rotate-180" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span>{content.gallery.productsPageBackText || "חזרה לדף הבית"}</span>
              </a>
            </div>

            {/* Page Header */}
            <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto" dir="rtl">
              <h1
                className="brand-font text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] leading-tight mb-4 text-brand"
              >
                {content.gallery.productsPageTitle || "השירותים שלנו"}
              </h1>
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-brand/40 to-transparent rounded-full mx-auto mb-5" />
              <p className="text-ink-muted text-sm sm:text-base md:text-lg leading-relaxed max-w-xl mx-auto">
                {content.gallery.productsPageSubtitle || "מגוון טיפולים מקצועיים המותאמים אישית לצרכי הארגון והעובדים שלכם"}
              </p>
            </div>

            <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item, idx) => {
                const mediaType = inferMediaType(item.image);
                const altText = item.title ?? "שירות עיסוי מקצועי";
                return (
                <div 
                  key={idx} 
                  className="group rounded-3xl border border-border bg-white shadow-sm overflow-hidden transition hover:shadow-lg"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                      {mediaType === 'video' ? (
                        <video
                          src={item.image}
                          className="h-full w-full object-cover"
                          playsInline
                          controls
                          muted
                          loop
                          preload="metadata"
                        />
                      ) : (
                    <ProgressiveImage
                      src={item.image}
                          alt={altText}
                      wrapperClassName="h-full w-full"
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                      skeletonClassName="bg-gradient-to-br from-[#f6eee4] via-[#f1dfc8] to-[#e6c9a8]"
                      loading="lazy"
                      decoding="async"
                      sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw"
                      width={600}
                      height={800}
                    />
                      )}
                  </div>
                  {(item.title || item.description) && (
                    <div className="px-4 py-4 rtl text-right" dir="rtl">
                      {item.title && <div className="font-semibold text-xl mb-2 text-brand">{item.title}</div>}
                      {item.description && <div className="text-ink-muted text-base">{item.description}</div>}
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <a href="/#contact" className="btn-brown inline-block">
                {content.gallery.productsPageCta || "צור קשר לפרטים נוספים"}
              </a>
            </div>
          </div>
        </Container>
      </Section>
      </main>

      <footer role="contentinfo" className="py-10 border-t border-border mt-10 bg-surface">
        <Container className="flex items-center justify-between">
          <div className="text-sm text-ink-muted">© {new Date().getFullYear()} {content.brand}. {content.ui?.footer?.copyright || "All rights reserved"}</div>
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-sm menu-font hover:opacity-80 transition-opacity cursor-pointer"
          >
            {content.ui?.footer?.backToTop || "Back to top ↑"}
          </button>
        </Container>
      </footer>
    </div>
  );
};

export default ProductsPage;

